import { Component, Injector } from '@angular/core';
import type { OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { ViewBasePage } from '@shared/pages';
import { AccountProfileService, DealershipService } from '@shared/services';
import type {
    TAccountProfile, TActivationInfo, TCreditInfo, TFeaturesInfo, TPeriodInfo,
    TDealership, TPricingInfo, TTimestamp
} from '@shared/models';
import { MyPhonePipe, MyTimestampPipe, MyCurrencyPipe } from '@shared/pipes';
import { Constants } from '@shared/models';
const DateTimeFormats = Constants.DateTimeFormats;

@Component({
    'selector': 'app-accountProfile',
    'templateUrl': './accountProfile.page.html',
    'styleUrls': ['./accountProfile.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule, MyPhonePipe, MyTimestampPipe, MyCurrencyPipe ]
})
export class AccountProfilePage extends ViewBasePage implements OnInit {
    // public accountProfile: TAccountProfile = {};
    public accountProfile: TAccountProfile;
    public dealership: TDealership;
    public activationInfo: TActivationInfo = {
        activationDate: this.dateService.getMinimumDate()
    };
    public featuresInfo: TFeaturesInfo = {};
    public periodInfo: TPeriodInfo;
    public pricingInfo: TPricingInfo = {};
    public creditInfo: TCreditInfo = { availableCredits: 0 };

    constructor(private accountProfileService: AccountProfileService, private dealershipService: DealershipService,
        injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_ACCOUNT_PROFILE);
    }

    public override async ngOnInit(): Promise<void> {
        try {
            this.dealership = this.dealershipService.getNullable();
            this.accountProfile = this.accountProfileService.getNullable();
            this.dealership = await this.dealershipService.get();
            this.accountProfile = await this.accountProfileService.get();
            this.activationInfo = this.accountProfile?.activationInfo;
            this.creditInfo = this.accountProfile?.creditInfo;
            this.featuresInfo = this.accountProfile?.featuresInfo;
            this.periodInfo = this.accountProfile?.periodInfo;
            this.pricingInfo = this.accountProfile?.pricingInfo;
            this.initMoreMenu();

            // const serverTime = '2022-09-20 18:00:00.000';        // 6 o'clock
            // const dateString = this.dateService.convertUtcToLocalTime(serverTime);
            // console.log('serverTime/dateString = ', serverTime, '/', dateString);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    public initMoreMenu(): void {
        this.resetPopoverMenu();
        this.pushPopoverMenuItem('Show Tour', 'show-account-profile-tour');
    }

    public toDate(timestamp: TTimestamp): string {
        const dateString = this.dateService.formatTimestamp(timestamp, 'MMMM do yyyy, hh:mm:ss a');
        return dateString;
    }

    public getActivationDate(): string {
        // const minimumDate = this.dateService.getMinimumDate();
        const activationDate = this.activationInfo.activationDate;
        const isMinimum = this.dateService.isMinimumDate(activationDate);

        // Apparently, in the Firebase Console when a Timestamp is set to minimum (Dec 31, 1969 4:00PM),
        // then in the typescript code we can compare this date to undefined!
        if (!activationDate || isMinimum) {
            return 'Not Activated';
        }

        const str = this.dateService.formatTimestamp(activationDate, DateTimeFormats.All);
        return str;
    }

    public getActivationOrderNumber(): string {
        const orderNumber = this.activationInfo.activationFeeOrderNumber;
        if (!orderNumber || orderNumber === 0) {
            return 'N.A.';
        } else {
            return orderNumber.toString();
        }
    }

    public getLockedInPricingStr(): string {
        const str = (this.featuresInfo?.lockedInPricing) ? 'Active' : 'Not Active';
        return str;
    }

    public getEfficiencyStr(): string {
        const str = (this.featuresInfo?.efficiency) ? 'Active' : 'Not Active';
        return str;
    }

    public getCustomerStr(): string {
        const str = (this.featuresInfo?.customer) ? 'Active' : 'Not Active';
        return str;
    }

    public getCampaignStr(): string {
        const str = (this.featuresInfo?.campaign) ? 'Active' : 'Not Active';
        return str;
    }

    // public getFeatureActiveStr(feature: boolean): string {
    //     const str = (feature) ? 'Active' : 'Not Active';
    //     return str;
    // }

    public async onResetClick(): Promise<void> {
        const minimumDate = this.dateService.getMinimumDate();

        this.accountProfile.activationInfo.activationDate = minimumDate;
        this.accountProfile.activationInfo.activationFeeOrderNumber = 0;

        this.accountProfile.packageName = 'Trial Package';
        this.accountProfile.status = 'In Trial Period';

        this.accountProfile.periodInfo.startDate = minimumDate;
        this.accountProfile.periodInfo.expirationDate = minimumDate;
        this.accountProfile.periodInfo.premiumSupportExpirationDate = minimumDate;
        this.accountProfile.periodInfo.webHostingExpirationDate = minimumDate;

        this.accountProfile.creditInfo.lastCreditPurchaseDate = minimumDate;
        this.accountProfile.creditInfo.availableCredits = 0;

        await this.accountProfileService.update(this.accountProfile);
    }
}
